/**
 * TTI — on-train ticket sales (Chris, 2026-10-28, rebuild item 9).
 *
 * Tickets the Travelling Ticket Inspectors sell on board never reach MERAC's
 * departure-by-departure sales, so the Studio's demand misses those
 * passengers. "TTI modelling" puts them back as an uplift on every day:
 *
 *   %       — add X% to each day's passengers, and the £ with them, pro rata:
 *             "if the day is 10,000 and my % is set at 10% you will add an
 *             additional 1000 and pro-rate the revenue and tickets up". The
 *             extra people follow the day's own sales curve and ticket split,
 *             because every departure and every untimed sale is scaled alike.
 *   iPlicit — the month's unclassified ticket sales from the iPlicit budget
 *             (a nominal Simon will provide) ÷ the standard fare = the extra
 *             passengers, spread the same way. NOT WIRED YET: until the nominal
 *             and its figures are in, this mode applies NO uplift and says so.
 *
 * ONE place applies it: withTti() over the Studio's sales payload as it
 * arrives, so the season build, the day-for-day valuation, the schedule, the
 * season table and the board paper all read the same uplifted demand — on
 * BOTH sides, because these passengers travel today too.
 */
import type { StudioPayload } from './types'

export interface TtiConfig {
  enabled: boolean
  mode: 'pct' | 'iplicit'
  /** % added to every day's passengers (and £), 'pct' mode. */
  pct: number
  /** The iPlicit budget nominal for unclassified ticket sales ('iplicit' mode). */
  nominal: string
}

export const DEFAULT_TTI: TtiConfig = { enabled: false, mode: 'pct', pct: 0, nominal: '' }

export function normaliseTti(v: unknown): TtiConfig {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<TtiConfig>
  const pct = Number(o.pct)
  return {
    enabled: o.enabled === true,
    mode: o.mode === 'iplicit' ? 'iplicit' : 'pct',
    pct: Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0,
    nominal: typeof o.nominal === 'string' ? o.nominal.trim().slice(0, 40) : '',
  }
}

/** The multiplier on passengers and £ — 1 when TTI is off, or in iPlicit mode
 *  (not wired yet). */
export function ttiFactor(cfg: TtiConfig): number {
  if (!cfg.enabled || cfg.mode !== 'pct' || !(cfg.pct > 0)) return 1
  return 1 + cfg.pct / 100
}

/** Plain-English line for the screens and the board paper; null when no uplift applies. */
export function ttiLabel(cfg: TtiConfig): string | null {
  if (!cfg.enabled) return null
  if (cfg.mode === 'iplicit') return `TTI from iPlicit${cfg.nominal ? ` (nominal ${cfg.nominal})` : ''} — not yet wired: no uplift applied`
  return cfg.pct > 0 ? `TTI uplift +${cfg.pct}% on every day's passengers and fares (both sides)` : null
}

type PaxNet = { pax: number; net: number }
const scale = <T extends PaxNet>(rows: T[] | undefined, f: number): T[] | undefined =>
  rows ? rows.map((r) => ({ ...r, pax: r.pax * f, net: r.net * f })) : rows

/** The sales payload with the TTI uplift applied — the same object back when
 *  none applies. Day counts (days_run, running_days) never change. */
export function withTti(p: StudioPayload, cfg: TtiConfig): StudioPayload {
  const f = ttiFactor(cfg)
  if (f === 1) return p
  return {
    ...p,
    demand: scale(p.demand, f)!,
    untimed: scale(p.untimed, f)!,
    prev_demand: scale(p.prev_demand, f)!,
    prev_untimed: scale(p.prev_untimed, f)!,
    slot_daily: scale(p.slot_daily, f),
    prev_slot_daily: scale(p.prev_slot_daily, f),
    untimed_daily: scale(p.untimed_daily, f),
    prev_untimed_daily: scale(p.prev_untimed_daily, f),
    prev2_untimed_daily: scale(p.prev2_untimed_daily, f),
    prev2_demand: scale(p.prev2_demand, f),
    prev2_untimed: scale(p.prev2_untimed, f),
    prev2_slot_daily: scale(p.prev2_slot_daily, f),
  }
}
